import { Box, Button, Chip, Theme, Typography } from "@mui/material";
import { AlertCircle } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import { useState } from "react";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import ZoomCallAiStatusInlineRow from "@/modules/zoom-calls/components/zoom-call-ai-status-inline-row";

const KEY_POINTS_PREVIEW = 3;

interface ZoomCallInfoProps {
  item: ActivityItem;
  theme: Theme;
  onViewDetailedCall?: () => void;
}

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const CARD_RADIUS = "12px";

const ZoomCallInfo = ({
  item,
  theme,
  onViewDetailedCall,
}: ZoomCallInfoProps) => {
  const [keyPointsExpanded, setKeyPointsExpanded] = useState(false);

  const sentiment = item.zoomCallSentiment;
  const flags = item.zoomCallFlags as Record<string, any> | null | undefined;
  const keyPoints = item.zoomCallSummary ?? [];

  const callOutcome = flags?.call_outcome as string | undefined;
  const redFlags = flags?.red_flags as string[] | undefined;
  const requiresEscalation = flags?.requires_escalation as boolean | undefined;
  const escalationReason = flags?.escalation_reason as string | undefined;

  const sentimentChips: Array<{ label: string; isArc?: boolean }> = [];
  if (sentiment?.overall_client)
    sentimentChips.push({ label: `Client ${sentiment.overall_client}` });
  if (sentiment?.overall_agent)
    sentimentChips.push({ label: `Agent ${sentiment.overall_agent}` });
  if (sentiment?.arc)
    sentimentChips.push({ label: sentiment.arc.replace(/_/g, " "), isArc: true });

  const hasSummaryContent = keyPoints.length > 0 || sentimentChips.length > 0;
  const hasAiStatusRow =
    item.zoomCallAiStatus === "completed" && !!item.zoomCallSuggestedStatusName;
  const hasAnyDetail =
    hasSummaryContent ||
    callOutcome ||
    requiresEscalation ||
    (redFlags && redFlags.length > 0) ||
    hasAiStatusRow;

  if (!hasAnyDetail) return null;

  return (
    <>
      {/* Summary card */}
      {hasSummaryContent && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            px: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xs,
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.primitives.white[200],
          }}
        >
          {/* "Call summary" label + sparkles */}
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              height: 20,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Call summary
            </Typography>
            <SparklesGradientIcon size={16} />
          </Box>

          {/* Sentiment */}
          {sentimentChips.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {sentimentChips.some((c) => !c.isArc) && (
                <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
                  {sentimentChips.filter((c) => !c.isArc).map((chip, idx) => (
                    <Chip
                      key={idx}
                      label={chip.label}
                      size="small"
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        color: theme.nawy.colors.semantic.text.primary,
                        backgroundColor: theme.nawy.colors.semantic.layer.primary,
                        borderRadius: "4px",
                        height: "auto",
                        maxWidth: "100%",
                        "& .MuiChip-label": {
                          px: theme.nawy.spacing.xs,
                          py: "2px",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              {sentimentChips.filter((c) => c.isArc).map((chip, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    ...theme.nawy.typography.english.variants.body.md.regular,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {chip.label}
                </Typography>
              ))}
            </Box>
          )}

          {/* Key points list */}
          {keyPoints.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {(keyPointsExpanded
                  ? keyPoints
                  : keyPoints.slice(0, KEY_POINTS_PREVIEW)
                ).map((point, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                      color: theme.nawy.colors.semantic.text.primary,
                    }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
              {keyPoints.length > KEY_POINTS_PREVIEW && (
                <Button
                  onClick={() => setKeyPointsExpanded((prev) => !prev)}
                  sx={{
                    mt: 0.5,
                    p: 0,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: theme.nawy.colors.semantic.text.accent,
                    minWidth: "auto",
                    "&:hover": { background: "transparent" },
                  }}
                >
                  {keyPointsExpanded
                    ? "View less"
                    : `View more (${keyPoints.length - KEY_POINTS_PREVIEW} more)`}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Call outcome card */}
      {callOutcome && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.primitives.white[200],
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "16px",
              color: theme.nawy.colors.semantic.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Call outcome
          </Typography>
          <Chip
            label={formatLabel(callOutcome)}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "16px",
              backgroundColor: theme.nawy.colors.semantic.indicator.blue,
              color: theme.nawy.colors.semantic.indicator.onColor,
              borderRadius: "4px",
              height: "auto",
              "& .MuiChip-label": { px: theme.nawy.spacing.xs, py: "2px" },
            }}
          />
        </Box>
      )}

      {/* Escalation banner */}
      {requiresEscalation && (
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor:
              theme.nawy.colors.semantic.layer.extension.negativeLight,
          }}
        >
          <AlertCircle
            size={16}
            color={theme.nawy.colors.semantic.indicator.red}
            style={{ flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "18px",
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            {escalationReason
              ? `Escalation ${escalationReason.charAt(0).toLowerCase()}${escalationReason.slice(1)}`
              : "Requires escalation"}
          </Typography>
        </Box>
      )}

      {/* Red flags card */}
      {redFlags && redFlags.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor:
              theme.nawy.colors.semantic.layer.extension.warningLight,
          }}
        >
          {redFlags.map((flag, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <AlertCircle
                size={16}
                color={theme.nawy.colors.semantic.indicator.orange}
                style={{ flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                {flag}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* AI suggested status inline row */}
      <ZoomCallAiStatusInlineRow
        aiStatus={
          item.zoomCallAiStatus
            ? {
                status: item.zoomCallAiStatus,
                suggestedStatusId: item.zoomCallSuggestedStatusId ?? null,
                suggestedStatusName: item.zoomCallSuggestedStatusName ?? null,
                suggestedStatusColor: item.zoomCallSuggestedStatusColor ?? null,
                oldStatusId: null,
                oldStatusName: item.zoomCallOldStatusName ?? null,
                oldStatusColor: item.zoomCallOldStatusColor ?? null,
                generatedAt: null,
              }
            : null
        }
        variant="full"
      />

      {/* View detailed call link — kept as-is */}
      {item.zoomCallUid && onViewDetailedCall && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetailedCall();
          }}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.text.accent,
            px: 0,
            minWidth: "auto",
            "&:hover": {
              background: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {"View detailed call →"}
        </Button>
      )}
    </>
  );
};

export default ZoomCallInfo;
